/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {Entity} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {capitalizeFirstLetter} from '@ion/core/src/utils/StringUtil';
import {PartialSuccessType} from '@ion/dls/src/types/types';
import {formatErrorMessage, partialStatusMessageFormatter} from '@ion/dls/src/utils/ComponentUtils';

/**
 * @UserPromptTypes
 *
 * Types that could be used on user prompt notifications to potentially change colors,
 * icons, etc.
 */
export enum UserPromptTypes {
  Error = 'error',
  Warning = 'warning',
  Success = 'success'
};

/**
 * @errorOptions
 *
 * Base options for error user prompts
 */
export const errorOptions = {
  display     : true,
  message     : 'common:errors.generic',
  actionLabel : 'common:retry',
  autoHide    : false,
  type        : UserPromptTypes.Error
};

/**
 * @successOptions
 *
 * Base options for success user prompts
 */
export const successOptions = {
  display             : true,
  message             : 'notifications:generic.success',
  actionLabel         : 'common:ok',
  autoHide            : true,
  partialSuccessState : null,
  type                : UserPromptTypes.Success
};

/**
 * Results for the successful add asset user prompt that extend the default options.
 *
 * ```typescript
 *
 *    dispatch(toggleUserPrompt(successAddOptions(Entity.role)));
 *
 * ```
 */
export const successAddOptions = (entity: Entity) => ({
  ...successOptions,
  message         : 'common:editors.shared.successCreated',
  translateOption : {entity: capitalizeFirstLetter(entity)}
});

/**
 * Results for the error add asset user prompt that extend the default options.
 *
 * ```typescript
 *
 *    dispatch(toggleUserPrompt(errorAddOptions(Entity.role)));
 *
 * ```
 */
export const errorAddOptions = (entity: Entity, err?: ServiceError) => ({

  ...errorOptions,
  message         : !isNil(err) ? formatErrorMessage(err) : 'common:editors.shared.errorCreated',
  translateOption : {entity: capitalizeFirstLetter(entity)},
  translateText   : isNil(err?.errorText)
});

/**
 * @successEditOptions
 *
 * Results for the successful edit asset user prompt that extend the default options.
 */
export const successEditOptions = (
  entity: Entity,
  partialSuccessState: PartialSuccessType = null,
  messageEntry = 'common:editors.shared.successEdited'
) => ({
  ...successAddOptions(entity),
  message             : messageEntry,
  partialSuccessState : partialSuccessState
    ? partialStatusMessageFormatter(partialSuccessState?.results, partialSuccessState?.items) : null
});

/**
 * @errorOptions
 *
 * Results for the error add asset user prompt that extend the default options.
 */
export const errorEditOptions = (entity: Entity, err?: ServiceError) => ({
  ...errorAddOptions(entity, err),
  message       : !isNil(err) ? formatErrorMessage(err) : 'common:editors.shared.errorEdited',
  translateText : isNil(err?.errorText)
});
